import { useMemo, useState } from 'react'
import { PageHead, Stat } from '../components/ui'
import { coverage, type CoverageStatus, type CoverageArea } from '../data/coverage'

const statusLabel: Record<CoverageStatus, string> = { web: 'ใช้ได้บนเว็บ', api: 'มี API', todo: 'ยังไม่ทำ', cancelled: 'ยกเลิก' }
const statusBadge: Record<CoverageStatus, string> = { web: 'closed', api: 'reviewing', todo: 'received', cancelled: 'received' }
const areaLabel: Record<CoverageArea, string> = {
  core: 'แกนระบบ', data: 'ข้อมูล', fraud: 'ตรวจจับทุจริต', security: 'ความปลอดภัย/กำกับ',
  saas: 'ปฏิบัติการ SaaS', ai: 'AI/ML', cloud: 'โครงสร้างพื้นฐาน',
}

export function Coverage() {
  const [status, setStatus] = useState<CoverageStatus | 'all'>('all')
  const [area, setArea] = useState<CoverageArea | 'all'>('all')
  const [q, setQ] = useState('')

  const counts = useMemo(() => ({
    web: coverage.filter((c) => c.status === 'web').length,
    api: coverage.filter((c) => c.status === 'api').length,
    todo: coverage.filter((c) => c.status === 'todo').length,
    cancelled: coverage.filter((c) => c.status === 'cancelled').length,
    total: coverage.filter((c) => c.status !== 'cancelled').length,
  }), [])

  const rows = useMemo(() => coverage.filter((c) =>
    (status === 'all' || c.status === status) &&
    (area === 'all' || c.area === area) &&
    (q.trim() === '' || String(c.n) === q.trim() || c.title.toLowerCase().includes(q.trim().toLowerCase())),
  ), [status, area, q])

  const pct = Math.round((counts.web / counts.total) * 100)

  const byArea = useMemo(() => {
    const keys = ['core', 'data', 'fraud', 'security', 'saas', 'ai', 'cloud'] as CoverageArea[]
    return keys.map((k) => {
      // cancelled parts stay out of the per-area denominator too, otherwise an area can never reach 100%
      const rows = coverage.filter((c) => c.area === k && c.status !== 'cancelled')
      return {
        area: k,
        total: rows.length,
        web: rows.filter((c) => c.status === 'web').length,
        api: rows.filter((c) => c.status === 'api').length,
        todo: rows.filter((c) => c.status === 'todo').length,
      }
    })
  }, [])

  return (
    <>
      <PageHead title="ความครอบคลุมระบบ" sub="183 ตอนที่อยู่ในขอบเขต (ตัด 1 ตอนตามคำสั่ง) เทียบกับสิ่งที่รันได้จริงในระบบนี้" />

      <div className="grid-4 stagger">
        <Stat label="ใช้ได้บนเว็บ" value={counts.web} unit={`/ ${counts.total}`} foot={`${pct}% ของทั้งหมด`} />
        <Stat label="มี API แต่ยังไม่มีหน้าจอ" value={counts.api} unit="ตอน" foot="เรียกใช้ได้ผ่าน API" />
        <Stat label="ยังไม่ได้ทำ" value={counts.todo} unit="ตอน" foot="ส่วนใหญ่เป็น infra ระดับองค์กร" />
        <Stat label="รวมที่ลงมือแล้ว" value={counts.web + counts.api} unit="ตอน"
          foot={`${Math.round(((counts.web + counts.api) / counts.total) * 100)}% ของ ${counts.total} ที่อยู่ในขอบเขต`} />
      </div>

      <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
        ตัวหารคือ {counts.total} ตอนที่อยู่ในขอบเขต — ไม่นับ {counts.cancelled} ตอนที่ยกเลิกตามคำสั่ง
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {(['all', 'web', 'api', 'todo'] as const).map((s) => (
            <button key={s} className={`chip${status === s ? ' chip-on' : ''}`} onClick={() => setStatus(s)}>
              {s === 'all' ? 'ทุกสถานะ' : statusLabel[s]}
            </button>
          ))}
          <span style={{ width: 12 }} />
          <select aria-label="กรองตามหมวด" className="input" style={{ width: 190, height: 32 }} value={area}
            onChange={(e) => setArea(e.target.value as CoverageArea | 'all')}>
            <option value="all">ทุกหมวด</option>
            {Object.entries(areaLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="input" style={{ width: 220, height: 32 }} placeholder="ค้นชื่อหรือเลขตอน"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>{rows.length} รายการ</span>
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">ความคืบหน้าตามหมวด</div>
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr><th>หมวด</th><th className="num">ทั้งหมด</th><th className="num">เว็บ</th><th className="num">API</th><th className="num">ยังไม่ทำ</th><th className="num">%</th></tr>
          </thead>
          <tbody>
            {byArea.map((r) => (
              <tr key={r.area}>
                <td>{areaLabel[r.area]}</td>
                <td className="num">{r.total}</td>
                <td className="num">{r.web}</td>
                <td className="num">{r.api}</td>
                <td className="num dim">{r.todo}</td>
                <td className="num">{r.total ? Math.round((r.web / r.total) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-wrap card" style={{ marginTop: 16 }}>
        <table className="data">
          <thead>
            <tr><th style={{ width: 64 }} className="num">ตอน</th><th>หัวข้อ</th><th style={{ width: 150 }}>หมวด</th><th style={{ width: 130 }}>สถานะ</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.n}>
                <td className="num dim">{c.n}</td>
                <td>
                  {c.title}
                  {c.simulated && (
                    <span className="badge badge-reviewing" style={{ marginLeft: 8 }} title="ตรรกะจริง แต่ผลข้างเคียงจำลอง">
                      จำลอง
                    </span>
                  )}
                </td>
                <td className="dim">{areaLabel[c.area]}</td>
                <td><span className={`badge badge-${statusBadge[c.status]}`}>{statusLabel[c.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
        ตัวเลขนี้คำนวณจากรายการเดียวกับที่ใช้ในรายงานสถานะ จึงไม่คลาดเคลื่อนจากการนับด้วยมือ
      </div>
    </>
  )
}
